// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect } from 'react';
import { Modal } from './Modal';
import { AppIcon } from './AppIcon';
import { isMobileDevice, downloadBlobUrl } from '../utils/mobileDownload';
import './ViewLoAModal.css';

const PDF_VIEWER_PARAMS = 'toolbar=0&navpanes=0&scrollbar=0';

// Chromium PDF viewer reads these hints from the URL fragment. Blob URLs from
// URL.createObjectURL never carry one, but guard against future callers anyway.
const buildPdfSrc = (url: string): string => {
  const sep = url.includes('#') ? '&' : '#';
  return `${url}${sep}${PDF_VIEWER_PARAMS}`;
};

export interface ViewLoAModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly documentUrl: string | null;
  readonly title: string;
  readonly loading?: boolean;
  /** Filename used when downloading on mobile instead of viewing inline. */
  readonly downloadFilename?: string;
  /**
   * When true, render the inline modal + iframe on mobile instead of the default
   * auto-download shortcut. Use this where the caller wants viewing and downloading
   * to be two distinct actions on every device (e.g. client-ui Documents/MyPolicies,
   * where a separate download button already exists).
   */
  readonly forceModalOnMobile?: boolean;
  /**
   * Extra class names appended to the modal content area. Lets a specific
   * caller (e.g. client-ui Documents) restyle the viewer surface without
   * affecting other consumers of this shared modal.
   */
  readonly contentClassName?: string;
  /**
   * MIME type of the document being shown. When it is an image (`image/*`),
   * the document is rendered with an `<img>` element instead of the PDF
   * `<iframe>`, so uploaded images display reliably. Defaults to PDF behaviour
   * when omitted.
   */
  readonly contentType?: string;
}

export function ViewLoAModal({
  open,
  onClose,
  documentUrl,
  title,
  loading = false,
  downloadFilename,
  forceModalOnMobile = false,
  contentClassName = '',
  contentType,
}: ViewLoAModalProps) {
  const isImage = contentType?.startsWith('image/') ?? false;
  // On mobile, skip the modal and trigger a download once the blob URL is ready
  useEffect(() => {
    if (forceModalOnMobile) return;
    if (!open || loading || !documentUrl) return;
    if (!isMobileDevice()) return;

    const filename = downloadFilename || `${title.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
    downloadBlobUrl(documentUrl, filename);
    onClose();
  }, [open, loading, documentUrl, downloadFilename, title, onClose, forceModalOnMobile]);

  // On mobile, show nothing (the useEffect above handles download + close)
  if (!forceModalOnMobile && isMobileDevice()) {
    // Still render a minimal loading modal while the blob is being fetched
    if (open && loading) {
      return (
        <Modal open onClose={onClose} size="small" className="view-loa-modal">
          <Modal.Content className={`view-loa-modal__content ${contentClassName}`.trim()}>
            <div className="view-loa-modal__loading">
              <div className="view-loa-modal__spinner" />
              <p>Preparing download...</p>
            </div>
          </Modal.Content>
        </Modal>
      );
    }
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xlarge"
      className="view-loa-modal"
    >
      <Modal.Header icon="file pdf outline" title={title} onClose={onClose} />
      <Modal.Content className="view-loa-modal__content">
        {loading ? (
          <div className="view-loa-modal__loading">
            <div className="view-loa-modal__spinner" />
            <p>Loading document...</p>
          </div>
        ) : documentUrl && isImage ? (
          <div className="view-loa-modal__image-wrap">
            <img
              src={documentUrl}
              className="view-loa-modal__image"
              alt={title}
            />
          </div>
        ) : documentUrl ? (
          <iframe
            src={buildPdfSrc(documentUrl)}
            className="view-loa-modal__iframe"
            title={`Document: ${title}`}
          />
        ) : (
          <div className="view-loa-modal__empty">
            <AppIcon name="file outline" size="huge" color="grey" />
            <p>Document not available.</p>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions
        primaryAction={{ label: 'Close', onClick: onClose }}
      />
    </Modal>
  );
}
